import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import Skeleton from "@mui/material/Skeleton";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import { LoansContext } from "../../context/LoansContext";
import { RootContext } from "../../context/RootContext";
import { fetchFieldAuditHistory } from "../../services/network";
import { AUDIT_RESOURCE_TYPE } from "../../constants/auditFields";
import { useUserDisplayName } from "../../hooks/useUserDisplayName";

import "./AuditTag.css";

type AuditTagProps = {
  field: string;
};

const formatTimestamp = (entry: any): string => {
  const raw =
    entry?.updatedOn ??
    entry?.updatedAt ??
    entry?.modifiedOn ??
    entry?.modifiedAt ??
    entry?.createdOn ??
    entry?.createdAt ??
    entry?.timestamp;
  if (!raw) return "";
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("MMM D, YYYY · h:mm A") : "";
};

const resolveUpdatedBy = (entry: any): string =>
  String(
    entry?.updatedBy ??
      entry?.updatedByName ??
      entry?.modifiedBy ??
      entry?.createdBy ??
      entry?.userName ??
      entry?.userId ??
      "",
  );

const KEYCLOAK_USER_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A timeline row for one audit entry. Its own component (rather than inline
// in the entries.map below) purely so it can call useUserDisplayName — the
// backend's updatedBy is sometimes a Keycloak user id, same as waivers'
// createdBy/responseBy (see WaiverSummary.tsx), and hooks can't be called
// inside a .map callback.
function AuditTrailItem({
  entry,
  isCurrent,
  hasNextItem,
}: Readonly<{ entry: any; isCurrent: boolean; hasNextItem: boolean }>) {
  const { t } = useTranslation();
  const rawUpdatedBy = resolveUpdatedBy(entry);
  const isLikelyUserId = KEYCLOAK_USER_ID_PATTERN.test(rawUpdatedBy);
  const resolvedName = useUserDisplayName(isLikelyUserId ? rawUpdatedBy : undefined);
  const updatedBy = isLikelyUserId ? resolvedName : rawUpdatedBy;

  return (
    <div className="audit-trail-item">
      <div className="audit-trail-marker">
        <span className={`audit-trail-dot ${isCurrent ? "audit-trail-dot-current" : ""}`} />
        {hasNextItem ? <span className="audit-trail-line" /> : null}
      </div>
      <div className="audit-trail-content">
        <div className="audit-trail-value-row">
          {/* A dropDown field's audited value is recorded as its option's
              i18n label key (see useLoanSizingHandler's getAuditDisplayValue)
              rather than the raw stored code — t() resolves it to the same
              text DropDown.tsx shows live. For any non-key value (numbers,
              free text, dates), t() has no matching translation and falls
              back to the string itself, so this is safe unconditionally. */}
          <span className="audit-trail-value">{t(String(entry?.value ?? ""))}</span>
          {isCurrent ? <span className="audit-trail-current-badge">CURRENT</span> : null}
        </div>
        {updatedBy ? <div className="audit-trail-updated-by">{updatedBy}</div> : null}
        {formatTimestamp(entry) ? (
          <div className="audit-trail-timestamp">{formatTimestamp(entry)}</div>
        ) : null}
      </div>
    </div>
  );
}

// History icon shown next to a field's label once it has any audit history.
// The last-3 entries are fetched lazily on hover (there's no bulk "full
// history" endpoint) and cached in context so repeat hovers don't refetch.
export default function AuditTag({ field }: Readonly<AuditTagProps>) {
  const { loanId } = useParams<{ loanId: string }>();
  const { fieldAuditHistoryCache, setFieldAuditHistoryCache } = useContext(LoansContext);
  const { dynamicConfig } = useContext(RootContext);
  const [loading, setLoading] = useState(false);

  const history = fieldAuditHistoryCache?.[field];
  const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };

  const handleFetchHistory = async () => {
    if ((Array.isArray(history) && history.length > 0) || loading || !loanId) return;
    setLoading(true);
    try {
      const { data } = await fetchFieldAuditHistory(
        AUDIT_RESOURCE_TYPE,
        loanId,
        field,
        3,
        0,
        realmHeaders,
      );
      // The response is keyed by the exact field path requested, e.g.
      // { "data.loanInformation.numberOfProperties": [entry, entry, ...] } —
      // newest first.
      const entries = Array.isArray(data?.[field]) ? data[field] : [];
      setFieldAuditHistoryCache?.((prev: Record<string, any[]>) => ({
        ...prev,
        [field]: entries,
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <div className="audit-trail-card" data-testid="audit-tag-skeleton">
          {[0, 1, 2].map((idx) => (
            <Skeleton key={idx} variant="text" width={160} sx={{ bgcolor: "grey.300" }} />
          ))}
        </div>
      );
    }
    // The initial-audit seed is recorded with action "create"; only an
    // "update" entry represents a real edit. `updatedBy` isn't a reliable
    // signal on its own — the audit service can leave it null even on
    // "update" entries — so a field that's only ever been seeded (no
    // "update" action at all) should read as having no audit history.
    const entries = history ?? [];
    const hasRealEdit = entries.some((entry: any) => entry?.action === "update");
    if (!hasRealEdit) {
      return <div className="audit-trail-card audit-trail-empty">No audit history</div>;
    }

    return (
      <div className="audit-trail-card">
        <div className="audit-trail-header">
          <span className="audit-trail-title">Audit Trail</span>
          <span className="audit-trail-subtitle">
            Last {entries.length} edit{entries.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="audit-trail-divider" />
        <div className="audit-trail-timeline">
          {entries.map((entry: any, idx: number) => (
            <AuditTrailItem
              key={entry?.key ?? idx}
              entry={entry}
              isCurrent={idx === 0}
              hasNextItem={idx < entries.length - 1}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      onOpen={handleFetchHistory}
      arrow
      placement="right-start"
      slots={{ transition: Fade }}
      slotProps={{
        transition: { timeout: 600 },
        tooltip: { sx: { backgroundColor: "transparent", padding: 0, maxWidth: "none" } },
      }}
    >
      <HistoryOutlinedIcon className="audit-tag-icon" style={{ width: "16px" }}/>
    </Tooltip>
  );
}
